import React, { useState } from 'react';
import {
  FiDownload,
  FiImage,
  FiFileText,
  FiMusic,
  FiPlay,
  FiX,
  FiMaximize2,
  FiTag,
  FiEye,
  FiExternalLink,
  FiLoader,
  FiArchive,
  FiAlertTriangle,
} from 'react-icons/fi';
import type { ChatMessage, ChatAttachment } from '../../api/conversations';
import { offerUuidFromMessage } from './offerCard';
import { OfferChatCard } from './OfferChatCard';
import { useConversationOffer } from './useConversationOffer';
import { ChatAvatar, pickDisplayName } from './ChatAvatar';
import { PdfPagePreview } from './PdfPagePreview';
import { PdfReader } from './PdfReader';
import { triggerFileDownload } from '../../utils/download';
import { describePdfError } from '../../utils/pdfPreview';
import type { PdfErrorKind } from '../../utils/pdfPreview';
import { Tooltip } from '../ui/Tooltip';

interface ChatMessageBubbleProps {
  message: ChatMessage;
  isCurrentUser: boolean;
  isClusteredWithPrev?: boolean;
  isClusteredWithNext?: boolean;
  senderAvatar?: string | null;
  isLastMessage?: boolean;
  isRead?: boolean;
  searchQuery?: string;
  isSearchFocused?: boolean;
  queueStatus?: 'pending' | 'uploading' | 'sending' | 'sent' | 'failed' | 'rate-limited';
  uploadProgress?: number;
  onRetry?: () => void;
  onAvatarClick?: () => void;
}

const DoubleCheckmark: React.FC<{ isRead?: boolean }> = ({ isRead = false }) => (
  <Tooltip content={isRead ? 'Read' : 'Delivered'}>
    <span
      className={`inline-flex items-center -space-x-1.5 ml-1 ${
        isRead ? 'text-[#00D45A]' : 'text-blue-200/70'
      }`}
      role="img"
      aria-label={isRead ? 'Read' : 'Delivered'}
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1.5 8.5L5 12L12.5 3.5"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <svg
        width="13"
        height="13"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="-ml-1.5"
      >
        <path
          d="M4.5 8.5L8 12L15.5 3.5"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  </Tooltip>
);

const ClockIcon: React.FC = () => (
  <Tooltip content="Sending">
    <span className="inline-flex items-center ml-1 text-zinc-400" role="img" aria-label="Sending">
      <svg
        width="12"
        height="12"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.8" />
        <path
          d="M8 4.5V8L10.5 9.5"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  </Tooltip>
);

const FailedIcon: React.FC<{ onRetry?: () => void }> = ({ onRetry }) => (
  <Tooltip content="Failed to send. Tap to retry.">
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onRetry?.();
      }}
      className="inline-flex items-center ml-1 text-red-400 hover:text-red-300 cursor-pointer"
      aria-label="Failed to send. Tap to retry."
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8 4.5V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="8" cy="11.5" r="1" fill="currentColor" />
      </svg>
    </button>
  </Tooltip>
);

function formatMessageTime(isoString?: string): string {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';
  return date
    .toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
    .toLowerCase();
}

function renderHighlightedText(text: string, searchQuery?: string) {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);

  const highlightMatches = (content: string) => {
    if (!searchQuery || !searchQuery.trim()) return content;
    const query = searchQuery.trim();
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const chunks = content.split(regex);

    return chunks.map((chunk, index) => {
      if (chunk.toLowerCase() === query.toLowerCase()) {
        return (
          <mark
            key={index}
            className="bg-amber-400 text-black px-1 py-0.5 rounded font-bold shadow-sm"
          >
            {chunk}
          </mark>
        );
      }
      return chunk;
    });
  };

  return parts.map((part, i) => {
    if (part.match(urlRegex)) {
      return (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="underline break-all hover:opacity-80 transition-opacity"
        >
          {highlightMatches(part)}
        </a>
      );
    }
    return <React.Fragment key={i}>{highlightMatches(part)}</React.Fragment>;
  });
}

// ── File Type Detection ────────────────────────────────────────────────────

function getAttachmentMimeType(att: ChatAttachment): string {
  if (att.mime_type) return att.mime_type;

  // Resolves through name → original_filename → public_id → url → mime_type,
  // so attachments that come back from the API without a name still classify.
  const ext = resolveAttachmentIdentity(att).ext;

  const mimeMap: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    avi: 'video/x-msvideo',
    webm: 'video/webm',
    mkv: 'video/x-matroska',
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    ogg: 'audio/ogg',
    m4a: 'audio/mp4',
    flac: 'audio/flac',
    aac: 'audio/aac',
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    zip: 'application/zip',
    rar: 'application/x-rar-compressed',
  };

  // If extension is known, always use extension mapping first for accurate classification
  if (ext && mimeMap[ext]) {
    return mimeMap[ext];
  }

  // Fallback to Cloudinary resource_type if extension is missing/unknown
  if (att.resource_type === 'image') return 'image/jpeg';
  if (att.resource_type === 'video') return 'video/mp4';

  return 'application/octet-stream';
}

type FileCategory = 'image' | 'video' | 'audio' | 'document';

function getFileCategory(mimeType: string, attName?: string): FileCategory {
  const ext = attName ? attName.split('.').pop()?.toLowerCase() : '';
  const documentExts = [
    'pdf',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'ppt',
    'pptx',
    'txt',
    'csv',
    'zip',
    'rar',
  ];

  if (ext && documentExts.includes(ext)) return 'document';
  if (
    mimeType === 'application/pdf' ||
    mimeType.includes('officedocument') ||
    mimeType.includes('msword')
  )
    return 'document';
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  return 'document';
}

function formatFileSize(bytes?: number): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Attachment Preview Components ──────────────────────────────────────────

const ImageAttachment: React.FC<{
  att: ChatAttachment;
  isCurrentUser: boolean;
  onPreview: () => void;
}> = ({ att, isCurrentUser, onPreview }) => {
  const [loaded, setLoaded] = useState(false);
  const url = att.url || att.file_url || '';

  return (
    <div onClick={onPreview} className="block group/img cursor-pointer">
      <div className="relative rounded-xl overflow-hidden w-full max-w-[280px] border border-white/10">
        {!loaded && (
          <div className="w-[280px] max-w-full h-[180px] bg-white/5 animate-pulse flex items-center justify-center">
            <FiImage size={24} className="text-zinc-600" />
          </div>
        )}
        <img
          src={url}
          alt={att.name || 'Image'}
          className={`w-full max-w-[280px] max-h-[300px] object-cover transition-opacity ${loaded ? 'opacity-100' : 'opacity-0 h-0'}`}
          onLoad={() => setLoaded(true)}
        />
        <div
          className="absolute top-2 right-2 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip content="Enlarge Preview">
            <button
              onClick={onPreview}
              className={`p-1.5 rounded-full backdrop-blur-md transition-colors ${isCurrentUser ? 'bg-black/40 hover:bg-black/60' : 'bg-white/10 hover:bg-white/20'}`}
              aria-label="Enlarge Preview"
            >
              <FiMaximize2 size={14} className="text-white" />
            </button>
          </Tooltip>
          <Tooltip content="Download">
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                triggerFileDownload(url, att.name);
              }}
              className={`p-1.5 rounded-full backdrop-blur-md transition-colors cursor-pointer ${isCurrentUser ? 'bg-black/40 hover:bg-black/60' : 'bg-white/10 hover:bg-white/20'}`}
              aria-label="Download"
            >
              <FiDownload size={14} className="text-white" />
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

const VideoAttachment: React.FC<{ att: ChatAttachment; onPreview: () => void }> = ({
  att,
  onPreview,
}) => {
  const url = att.url || att.file_url || '';

  return (
    <div className="relative rounded-xl overflow-hidden w-full max-w-[320px] border border-white/10 bg-black">
      <button onClick={onPreview} className="relative w-full cursor-pointer group/vid text-left">
        {/* Video thumbnail — use Cloudinary poster by replacing extension */}
        <video
          src={url}
          className="w-full max-w-[320px] max-h-[240px] object-cover"
          muted
          preload="metadata"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover/vid:bg-black/30 transition-colors">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
            <FiPlay size={20} className="text-white ml-0.5" fill="white" />
          </div>
        </div>
        {att.name && (
          <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-gradient-to-t from-black/80 to-transparent flex items-center justify-between">
            <p className="text-[11px] text-white/80 truncate">{att.name}</p>
            <Tooltip content="Download video">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  triggerFileDownload(url, att.name);
                }}
                className="p-1 rounded-full hover:bg-white/20 text-white transition-colors"
                aria-label="Download video"
              >
                <FiDownload size={12} />
              </button>
            </Tooltip>
          </div>
        )}
      </button>
    </div>
  );
};

const AudioAttachment: React.FC<{ att: ChatAttachment; isCurrentUser: boolean }> = ({
  att,
  isCurrentUser,
}) => {
  const url = att.url || att.file_url || '';
  const fileName = att.name || 'Audio';
  const size = formatFileSize(att.file_size);

  return (
    <div
      className={`rounded-xl overflow-hidden ${isCurrentUser ? 'bg-black/15' : 'bg-[#1C1C1E] border border-[#3A3A3C]'}`}
    >
      <div className="flex items-center gap-3 px-3 pt-3 pb-1">
        <div className={`p-2 rounded-lg ${isCurrentUser ? 'bg-white/10' : 'bg-[#2A2A2D]'}`}>
          <FiMusic size={16} className={isCurrentUser ? 'text-white' : 'text-[#1784FF]'} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-medium truncate">{fileName}</p>
          {size && (
            <p className={`text-[11px] ${isCurrentUser ? 'text-blue-200' : 'text-zinc-500'}`}>
              {size}
            </p>
          )}
        </div>
        <Tooltip content="Download">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              triggerFileDownload(url, fileName);
            }}
            className="p-1.5 rounded-full hover:bg-white/10 transition-colors cursor-pointer"
            aria-label="Download"
          >
            <FiDownload size={14} className={isCurrentUser ? 'text-white' : 'text-[#1784FF]'} />
          </button>
        </Tooltip>
      </div>
      <div className="px-3 pb-3 pt-1">
        <audio
          src={url}
          controls
          className="w-full h-8"
          style={{
            filter: isCurrentUser
              ? 'invert(1) hue-rotate(180deg) brightness(0.85)'
              : 'invert(1) hue-rotate(180deg) brightness(0.7)',
          }}
          preload="metadata"
        />
      </div>
    </div>
  );
};

// ── Document Type Metadata ─────────────────────────────────────────────────
// Per-type accent colors, labels and preview strategy so the UI reflects the
// actual file instead of treating everything as a PDF.

/** Extension implied by a mime type, for attachments that carry no filename. */
const MIME_TO_EXT: Record<string, string> = {
  'application/pdf': 'pdf',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'application/zip': 'zip',
  'application/x-rar-compressed': 'rar',
  'text/plain': 'txt',
  'text/csv': 'csv',
};

const KNOWN_EXT_RE = /\.(pdf|docx?|xlsx?|pptx?|txt|csv|zip|rar)$/i;

const SHARED_FILE_PREFIX = 'Shared a file:';

/**
 * Recover the original filename from the auto-generated message body.
 *
 * The API drops the uploaded `name` and returns `original_filename: ""`, but
 * the sender's own placeholder body ("Shared a file: report.pdf") still
 * carries it — otherwise the UI can only fall back to the Cloudinary hash.
 */
function extractSharedFileName(body?: string): string | undefined {
  if (!body?.startsWith(SHARED_FILE_PREFIX)) return undefined;
  const name = body.slice(SHARED_FILE_PREFIX.length).trim();
  return name || undefined;
}

/** Strip query/hash and return the last path segment. */
function basenameFromUrl(url: string): string {
  if (!url) return '';
  try {
    const path = url.split('?')[0].split('#')[0];
    return decodeURIComponent(path.split('/').pop() || '');
  } catch {
    return '';
  }
}

/**
 * Work out a display name and extension for an attachment.
 *
 * The backend does not persist the uploaded `name`, so on refetch only
 * `public_id` / `url` / `mime_type` survive. Each is tried in turn rather than
 * letting every document degrade to a generic "File".
 */
function resolveAttachmentIdentity(att: ChatAttachment): { fileName: string; ext: string } {
  const url = att.url || att.file_url || '';

  const candidates = [
    att.original_filename,
    att.name,
    basenameFromUrl(att.public_id || ''),
    basenameFromUrl(url),
  ];

  // Prefer the first candidate that actually carries a recognisable extension.
  for (const candidate of candidates) {
    if (candidate && KNOWN_EXT_RE.test(candidate)) {
      return { fileName: candidate, ext: candidate.split('.').pop()!.toLowerCase() };
    }
  }

  // No extension anywhere — fall back to the mime type, appending the implied
  // extension to whichever bare name we have.
  const mimeExt = att.mime_type ? MIME_TO_EXT[att.mime_type.toLowerCase()] : undefined;
  const bareName = candidates.find((c) => !!c && c.trim().length > 0);

  if (mimeExt) {
    return { fileName: bareName ? `${bareName}.${mimeExt}` : `Document.${mimeExt}`, ext: mimeExt };
  }

  if (bareName) {
    const ext = bareName.includes('.') ? bareName.split('.').pop()!.toLowerCase() : '';
    return { fileName: bareName, ext };
  }

  return { fileName: '', ext: '' };
}

type DocKind = 'pdf' | 'word' | 'excel' | 'powerpoint' | 'text' | 'archive' | 'generic';

interface DocMeta {
  kind: DocKind;
  label: string; // Short badge label, e.g. "PDF", "DOC"
  noun: string; // Human-readable type, e.g. "PDF", "Spreadsheet"
  /** Icon container classes (bg + border + text color). */
  iconClass: string;
  /** Solid badge background for the extension tag / viewer chip. */
  badgeClass: string;
}

function getDocMeta(extOrFileName: string): DocMeta {
  // Accepts either a bare extension ("pdf") or a full filename.
  const raw = (extOrFileName || '').split('?')[0];
  const ext = (raw.includes('.') ? raw.split('.').pop()! : raw).toLowerCase();

  switch (ext) {
    case 'pdf':
      return {
        kind: 'pdf',
        label: 'PDF',
        noun: 'PDF',
        iconClass: 'bg-red-500/15 border-red-500/25 text-red-500',
        badgeClass: 'bg-red-600',
      };
    case 'doc':
    case 'docx':
      return {
        kind: 'word',
        label: 'DOC',
        noun: 'Word Document',
        iconClass: 'bg-blue-500/15 border-blue-500/25 text-blue-400',
        badgeClass: 'bg-blue-600',
      };
    case 'xls':
    case 'xlsx':
      return {
        kind: 'excel',
        label: 'XLS',
        noun: 'Spreadsheet',
        iconClass: 'bg-emerald-500/15 border-emerald-500/25 text-emerald-400',
        badgeClass: 'bg-emerald-600',
      };
    case 'csv':
      return {
        kind: 'text',
        label: 'CSV',
        noun: 'CSV File',
        iconClass: 'bg-emerald-500/15 border-emerald-500/25 text-emerald-400',
        badgeClass: 'bg-emerald-600',
      };
    case 'ppt':
    case 'pptx':
      return {
        kind: 'powerpoint',
        label: 'PPT',
        noun: 'Presentation',
        iconClass: 'bg-orange-500/15 border-orange-500/25 text-orange-400',
        badgeClass: 'bg-orange-600',
      };
    case 'txt':
      return {
        kind: 'text',
        label: 'TXT',
        noun: 'Text File',
        iconClass: 'bg-zinc-500/15 border-zinc-500/25 text-zinc-300',
        badgeClass: 'bg-zinc-600',
      };
    case 'zip':
    case 'rar':
      return {
        kind: 'archive',
        label: ext.toUpperCase(),
        noun: 'Archive',
        iconClass: 'bg-amber-500/15 border-amber-500/25 text-amber-400',
        badgeClass: 'bg-amber-600',
      };
    default:
      return {
        kind: 'generic',
        label: (ext || 'FILE').toUpperCase().slice(0, 4),
        noun: 'File',
        iconClass: 'bg-zinc-500/15 border-zinc-500/25 text-zinc-300',
        badgeClass: 'bg-zinc-600',
      };
  }
}

type DocPreviewMode = 'pdf' | 'office' | 'text' | 'none';

/** Decide how (and whether) a document can be previewed inline. */
function getDocumentPreview(url: string, kind: DocKind): { mode: DocPreviewMode; src: string } {
  if (!url) return { mode: 'none', src: '' };

  const isRemote = /^https?:\/\//i.test(url);
  const gview = `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(url)}`;

  if (kind === 'pdf') {
    // Cloudinary raw-delivered PDFs don't render inline reliably; use the viewer.
    if (isRemote && url.includes('/raw/upload/')) return { mode: 'office', src: gview };
    return { mode: 'pdf', src: url };
  }

  // Office formats can't be rendered natively — the Google viewer needs a public URL.
  if (kind === 'word' || kind === 'excel' || kind === 'powerpoint') {
    return isRemote ? { mode: 'office', src: gview } : { mode: 'none', src: url };
  }

  // Plain text / CSV render fine in a sandboxed iframe.
  if (kind === 'text') return { mode: 'text', src: url };

  return { mode: 'none', src: url };
}

function getDocumentThumbnailUrl(url: string): string | null {
  if (!url || url.startsWith('blob:') || url.startsWith('data:')) return null;
  if (url.includes('/raw/upload/')) return null;

  if (url.includes('res.cloudinary.com')) {
    const parts = url.split('/upload/');
    if (parts.length === 2) {
      let path = parts[1];
      const match = path.match(/\.(pdf|doc|docx|ppt|pptx|xls|xlsx)$/i);
      if (match) {
        path = path.slice(0, match.index) + '.jpg';
        return `${parts[0]}/upload/c_fill,w_500,h_320,g_north,pg_1,f_jpg/${path}`;
      }
    }
  }
  return null;
}

const DocumentAttachment: React.FC<{
  att: ChatAttachment;
  isCurrentUser: boolean;
  onPreview?: () => void;
}> = ({ att, isCurrentUser, onPreview }) => {
  const url = att.url || att.file_url || '';
  const identity = resolveAttachmentIdentity(att);
  const meta = getDocMeta(identity.ext);
  // Cloudinary public_ids are opaque hashes; show the type instead of a hash.
  const fileName = identity.fileName || meta.noun;
  const size = formatFileSize(att.file_size);
  const thumbUrl = getDocumentThumbnailUrl(url);
  const [thumbError, setThumbError] = useState(false);
  const [pdfFailed, setPdfFailed] = useState(false);
  const [pdfError, setPdfError] = useState<PdfErrorKind | null>(null);
  const [pageCount, setPageCount] = useState<number | null>(null);

  const handlePdfFailure = React.useCallback((kind: PdfErrorKind) => {
    setPdfFailed(true);
    setPdfError(kind);
  }, []);
  const handlePdfLoaded = React.useCallback((pages: number) => setPageCount(pages), []);

  // Real first-page render for PDFs; Cloudinary thumbnail only works for
  // image-delivered files, so it stays as a secondary option.
  const showPdfPreview = meta.kind === 'pdf' && !!url && !pdfFailed;
  const showThumb = !showPdfPreview && thumbUrl && !thumbError;
  const hasCover = showPdfPreview || showThumb;
  const DocIcon = meta.kind === 'archive' ? FiArchive : FiFileText;

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        if (onPreview) onPreview();
        else if (url) window.open(url, '_blank');
      }}
      className={`flex flex-col rounded-2xl overflow-hidden my-1 border transition-all group/doc cursor-pointer w-full max-w-[280px] sm:max-w-xs ${
        isCurrentUser
          ? 'bg-[#18181A] hover:bg-[#202023] border-white/15 text-white shadow-lg'
          : 'bg-[#1C1C1E] hover:bg-[#242427] border-[#3A3A3C] text-zinc-100 shadow-md'
      }`}
    >
      {/* Cover preview: rendered first page for PDFs, Cloudinary thumb otherwise */}
      {hasCover ? (
        <div className="relative w-full overflow-hidden border-b border-white/10 group-hover/doc:opacity-95 transition-opacity">
          {showPdfPreview ? (
            <PdfPagePreview url={url} onFailure={handlePdfFailure} onLoaded={handlePdfLoaded} />
          ) : (
            <div className="relative w-full h-36 sm:h-40 bg-[#252528] overflow-hidden">
              <img
                src={thumbUrl as string}
                alt={`${fileName} Preview`}
                className="w-full h-full object-cover object-top opacity-90 group-hover/doc:opacity-100 group-hover/doc:scale-105 transition-all duration-300"
                onError={() => setThumbError(true)}
              />
            </div>
          )}

          {/* Page-count chip, mirrors WhatsApp/Telegram document cards */}
          {pageCount !== null && (
            <span className="absolute top-2 right-2 text-[10px] font-semibold text-white bg-black/60 backdrop-blur-sm px-2 py-0.5 rounded-full border border-white/15">
              {pageCount} {pageCount === 1 ? 'page' : 'pages'}
            </span>
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex items-end justify-between p-2.5 opacity-0 group-hover/doc:opacity-100 transition-opacity pointer-events-none">
            <span className="text-[11px] font-semibold text-white bg-black/60 backdrop-blur-sm px-2.5 py-1 rounded-full flex items-center gap-1.5 border border-white/15">
              <FiMaximize2 size={12} />
              <span>Expand Preview</span>
            </span>
          </div>
        </div>
      ) : null}

      {/* Bottom Info Bar */}
      <div className="flex items-center gap-3 p-3">
        {/* Document Icon & File Tag Badge */}
        <div
          className={`relative flex items-center justify-center w-10 h-10 rounded-xl border shrink-0 group-hover/doc:scale-105 transition-transform ${meta.iconClass}`}
        >
          <DocIcon size={20} />
          {!hasCover && (
            <span
              className={`absolute -bottom-1 text-[7px] font-black tracking-wider text-white px-1 py-0.2 rounded uppercase ${meta.badgeClass}`}
            >
              {meta.label}
            </span>
          )}
        </div>

        {/* Document Title & Meta */}
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-bold truncate leading-tight text-white">{fileName}</p>
          <div className="flex items-center gap-2 mt-1">
            {size && (
              <span
                className={`text-[11px] font-medium ${isCurrentUser ? 'text-blue-200' : 'text-zinc-400'}`}
              >
                {size}
              </span>
            )}
            {pdfError === 'blocked' || pdfError === 'missing' ? (
              <span
                className="text-[11px] font-semibold text-amber-400 flex items-center gap-1"
                title={describePdfError(pdfError, meta.noun.toLowerCase())}
              >
                <FiAlertTriangle size={11} />
                {pdfError === 'blocked' ? 'Unavailable' : 'File missing'}
              </span>
            ) : (
              <span className="text-[11px] font-semibold text-[#1784FF] group-hover/doc:underline flex items-center gap-0.5">
                <FiEye size={11} /> View {meta.noun}
              </span>
            )}
          </div>
        </div>

        {/* Explicit Download Button */}
        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
          <Tooltip content="Download document">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                triggerFileDownload(url, fileName);
              }}
              className={`p-2 rounded-full transition-colors cursor-pointer ${
                isCurrentUser
                  ? 'hover:bg-white/15 text-white'
                  : 'hover:bg-white/10 text-zinc-400 hover:text-white'
              }`}
              aria-label="Download document"
            >
              <FiDownload size={15} />
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

// Shown when a document can't be embedded (archives, blob-only Office files,
// or a browser that declines to render the PDF object).
const DocumentPreviewFallback: React.FC<{
  name: string;
  url: string;
  meta: DocMeta;
  errorKind?: PdfErrorKind;
}> = ({ name, url, meta, errorKind }) => {
  const Icon = meta.kind === 'archive' ? FiArchive : FiFileText;
  const canOpen = meta.kind !== 'archive' && !!url;

  return (
    <div className="flex flex-col items-center justify-center h-full p-6 text-center bg-[#1C1C1E]">
      <div
        className={`flex items-center justify-center w-16 h-16 rounded-2xl border mb-4 ${meta.iconClass}`}
      >
        <Icon size={30} />
      </div>
      <p className="text-white font-bold text-base mb-1 max-w-xs truncate">{name}</p>
      <p className="text-zinc-400 text-xs mb-4 max-w-sm">
        {errorKind
          ? describePdfError(errorKind, meta.noun.toLowerCase())
          : canOpen
            ? `Preview isn't available here — open the ${meta.noun} to view it.`
            : `${meta.noun} files can't be previewed. Download to open it.`}
      </p>
      <div className="flex items-center gap-2">
        {canOpen && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="px-5 py-2.5 rounded-full bg-[#1784FF] text-white text-xs font-bold hover:bg-blue-500 transition-colors inline-flex items-center gap-1.5"
          >
            <span>Open in New Window</span>
            <FiExternalLink size={14} />
          </a>
        )}
        <button
          type="button"
          onClick={() => triggerFileDownload(url, name)}
          className="px-5 py-2.5 rounded-full bg-white/10 text-white text-xs font-bold hover:bg-white/20 transition-colors inline-flex items-center gap-1.5"
        >
          <FiDownload size={14} />
          <span>Download</span>
        </button>
      </div>
    </div>
  );
};

// ── Fullscreen Media Viewer Modal ───────────────────────────────────────────

export interface FullscreenMediaViewerProps {
  attachment: ChatAttachment;
  onClose: () => void;
}

export const FullscreenMediaViewer: React.FC<FullscreenMediaViewerProps> = ({
  attachment,
  onClose,
}) => {
  const url = attachment.url || attachment.file_url || '';
  const identity = resolveAttachmentIdentity(attachment);
  const mimeType = getAttachmentMimeType(attachment);
  const docMeta = getDocMeta(identity.ext);
  const name = identity.fileName || docMeta.noun;
  const category = getFileCategory(mimeType, identity.fileName);
  const docPreview = getDocumentPreview(url, docMeta.kind);

  // Close on Escape key
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[300] bg-black/95 backdrop-blur-sm flex flex-col justify-between"
      onClick={onClose}
    >
      {/* Header Info & Controls */}
      <div
        className="flex items-center justify-between p-4 bg-gradient-to-b from-black/80 to-transparent z-10 w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="min-w-0 flex-1 pr-4 flex items-center gap-3">
          <div>
            <h4 className="text-white text-[15px] font-semibold truncate">{name}</h4>
            {attachment.file_size && (
              <p className="text-zinc-400 text-[12px] mt-0.5">
                {formatFileSize(attachment.file_size)}
              </p>
            )}
          </div>
          {(category === 'document' || mimeType === 'application/pdf') && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1784FF] hover:bg-[#1784FF]/80 text-white text-xs font-semibold transition-colors shadow-sm cursor-pointer ml-2 shrink-0"
            >
              <span>Open {docMeta.noun} in New Tab</span>
              <FiExternalLink size={14} />
            </a>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Tooltip content="Download">
            <button
              onClick={(e) => {
                e.stopPropagation();
                triggerFileDownload(url, name);
              }}
              className="p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
              aria-label="Download"
            >
              <FiDownload size={20} />
            </button>
          </Tooltip>
          <Tooltip content="Close">
            <button
              onClick={onClose}
              className="p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
              aria-label="Close"
            >
              <FiX size={20} />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center p-4 min-h-0 relative select-none">
        <div
          className="max-w-full max-h-full flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          {category === 'image' && (
            <img
              src={url}
              alt={name}
              className="max-w-full max-h-[80vh] md:max-h-[85vh] object-contain rounded-lg shadow-2xl transition-transform duration-300"
            />
          )}
          {category === 'video' && (
            <video
              src={url}
              controls
              autoPlay
              className="max-w-full max-h-[80vh] md:max-h-[85vh] rounded-lg shadow-2xl"
            />
          )}
          {(category === 'document' || mimeType === 'application/pdf') && (
            <div className="w-[92vw] max-w-5xl h-[82vh] bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] shadow-2xl overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-4 py-2.5 bg-[#2A2A2D] border-b border-[#3A3A3C]">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className={`${docMeta.badgeClass} text-white text-[10px] font-bold px-2 py-0.5 rounded shrink-0`}
                  >
                    {docMeta.label}
                  </span>
                  <span className="text-white text-xs font-semibold truncate">{name}</span>
                </div>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#1784FF] hover:underline text-xs font-medium flex items-center gap-1 shrink-0 ml-2"
                >
                  <span>Open in new tab</span>
                  <FiExternalLink size={12} />
                </a>
              </div>

              {docMeta.kind === 'pdf' && url ? (
                <PdfReader
                  url={url}
                  fallback={(kind) => (
                    <DocumentPreviewFallback
                      name={name}
                      url={url}
                      meta={docMeta}
                      errorKind={kind}
                    />
                  )}
                />
              ) : docPreview.mode === 'office' || docPreview.mode === 'text' ? (
                <iframe
                  src={docPreview.src}
                  title={name}
                  className={`w-full flex-1 border-0 ${docPreview.mode === 'text' ? 'bg-white text-black' : 'bg-white'}`}
                />
              ) : (
                <DocumentPreviewFallback name={name} url={url} meta={docMeta} />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer space */}
      <div className="h-16 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────

export const ChatMessageBubble: React.FC<ChatMessageBubbleProps> = ({
  message,
  isCurrentUser,
  isClusteredWithPrev = false,
  isClusteredWithNext = false,
  senderAvatar,
  isRead = false,
  searchQuery,
  isSearchFocused = false,
  queueStatus,
  uploadProgress,
  onRetry,
  onAvatarClick,
}) => {
  const [previewAttachment, setPreviewAttachment] = useState<ChatAttachment | null>(null);
  const conversationOfferUuid = useConversationOffer(message);
  const timeStr = formatMessageTime(message.created_at);

  // ── Render Structured Card (e.g. Change Request / Offer Card) ───────────────
  if (message.kind === 'card' || message.card_type || message.card_payload) {
    const offerUuid = offerUuidFromMessage(message) ?? conversationOfferUuid;
    const payload = message.card_payload || {};
    const cardStatus = payload.status || message.card_type || 'Update';

    return (
      <div
        id={`msg-${message.id}`}
        className={`my-2 flex flex-col ${isCurrentUser ? 'items-end' : 'items-start'} w-full animate-in slide-in-from-bottom-2 fade-in duration-300 ${
          isSearchFocused
            ? 'ring-2 ring-amber-400 rounded-2xl p-1 shadow-[0_0_16px_rgba(251,191,36,0.4)]'
            : ''
        }`}
      >
        <div
          className={`relative w-full min-w-0 max-w-full sm:max-w-lg ${
            offerUuid
              ? ''
              : 'p-[1px] rounded-2xl bg-gradient-to-br from-[#1784FF]/40 via-[#3A3A3C] to-[#00D45A]/30'
          }`}
        >
          {offerUuid ? (
            <OfferChatCard message={message} offerUuid={offerUuid} />
          ) : (
            <div className="w-full h-full rounded-[15px] bg-[#1C1C1E] p-4 text-[14px] leading-relaxed shadow-lg">
              <div className="flex items-center justify-between gap-2 mb-2.5">
                {payload.title ? (
                  <p className="font-bold text-white text-[15px] flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#1784FF]"></span>
                    {payload.title}
                  </p>
                ) : (
                  <span className="text-xs font-bold uppercase tracking-wider text-[#1784FF]">
                    {message.card_type ? message.card_type.replace('_', ' ') : 'Card Details'}
                  </span>
                )}
                {cardStatus && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-[#1784FF]/15 text-[#1784FF] border border-[#1784FF]/30">
                    <FiTag size={10} />
                    <span className="capitalize">{cardStatus}</span>
                  </span>
                )}
              </div>

              {payload.requested_change && (
                <div className="mb-3 space-y-1 bg-white/5 p-2.5 rounded-xl border border-white/5">
                  <p className="text-[12px] font-semibold text-zinc-400 uppercase tracking-wider">
                    Requested a change
                  </p>
                  <p className="text-zinc-200 break-words [overflow-wrap:anywhere]">
                    {renderHighlightedText(payload.requested_change, searchQuery)}
                  </p>
                </div>
              )}

              {message.body && !payload.requested_change && (
                <div className="mb-3 space-y-1">
                  <p className="text-[12px] font-semibold text-zinc-400 uppercase tracking-wider">
                    {message.card_type === 'change_request' ? 'Requested a change' : 'Details'}
                  </p>
                  <p className="text-zinc-200 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                    {renderHighlightedText(message.body, searchQuery)}
                  </p>
                </div>
              )}

              {payload.proposed_terms && (
                <div className="space-y-1 border-t border-[#3A3A3C] pt-3 mt-3">
                  <p className="text-[12px] font-semibold text-zinc-400 uppercase tracking-wider">
                    Proposed terms
                  </p>
                  <p className="font-medium text-white break-words [overflow-wrap:anywhere]">
                    {renderHighlightedText(payload.proposed_terms, searchQuery)}
                  </p>
                </div>
              )}

              {payload.offer_amount && (
                <div className="flex items-center justify-between border-t border-[#3A3A3C] pt-3 mt-3">
                  <p className="text-[12px] font-semibold text-zinc-400 uppercase tracking-wider">
                    Offer
                  </p>
                  <p className="font-bold text-[#00D45A] text-lg bg-[#00D45A]/10 px-3 py-1 rounded-lg">
                    {payload.currency || '$'}
                    {payload.offer_amount}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-1">
          {!isClusteredWithNext && timeStr && (
            <span
              className={`text-[11px] text-zinc-500 font-medium ${isCurrentUser ? 'mr-1' : 'ml-1'}`}
            >
              {timeStr}
            </span>
          )}
          {isCurrentUser && <DoubleCheckmark isRead={isRead} />}
        </div>
      </div>
    );
  }

  // ── Render Attachments ──────────────────────────────────────────────────────
  const renderAttachments = () => {
    if (!message.attachments || message.attachments.length === 0) return null;

    // The auto-body names exactly one file, so only trust it for single
    // attachments. Applied here so every attachment type gets the real name.
    const nameHint =
      message.attachments.length === 1 ? extractSharedFileName(message.body) : undefined;

    return (
      <div className="space-y-2">
        {message.attachments.map((rawAtt, i) => {
          const att: ChatAttachment =
            nameHint && !rawAtt.name ? { ...rawAtt, name: nameHint } : rawAtt;

          const mimeType = getAttachmentMimeType(att);
          const category = getFileCategory(mimeType, att.name || att.url || att.file_url);

          switch (category) {
            case 'image':
              return (
                <ImageAttachment
                  key={i}
                  att={att}
                  isCurrentUser={isCurrentUser}
                  onPreview={() => setPreviewAttachment(att)}
                />
              );
            case 'video':
              return (
                <VideoAttachment key={i} att={att} onPreview={() => setPreviewAttachment(att)} />
              );
            case 'audio':
              return <AudioAttachment key={i} att={att} isCurrentUser={isCurrentUser} />;
            default:
              return (
                <DocumentAttachment
                  key={i}
                  att={att}
                  isCurrentUser={isCurrentUser}
                  onPreview={() => setPreviewAttachment(att)}
                />
              );
          }
        })}
      </div>
    );
  };

  const renderStatusIcon = () => {
    if (!isCurrentUser) return null;
    if (queueStatus === 'failed' || queueStatus === 'rate-limited') {
      return <FailedIcon onRetry={onRetry} />;
    }
    if (queueStatus === 'pending' || queueStatus === 'uploading' || queueStatus === 'sending') {
      return <ClockIcon />;
    }
    return <DoubleCheckmark isRead={isRead} />;
  };

  // Check if message body is just the auto-generated "Shared a file:" text
  const isAutoBody =
    message.body?.startsWith('Shared a file:') &&
    message.attachments &&
    message.attachments.length > 0;
  const hasTextBody = Boolean(message.body && !isAutoBody);

  // Check if bubble is attachment-only and contains only media (images/videos)
  const isAttachmentOnly =
    !hasTextBody && Boolean(message.attachments && message.attachments.length > 0);
  const isImageOrVideoOnly =
    isAttachmentOnly &&
    Boolean(
      message.attachments &&
      message.attachments.every((att) => {
        const mime = getAttachmentMimeType(att);
        const category = getFileCategory(mime);
        return category === 'image' || category === 'video';
      }),
    );

  // Use compact padding for media-only bubbles, standard padding for others
  const paddingClass = isImageOrVideoOnly ? 'p-[3px] overflow-hidden' : 'px-4 py-2.5';

  // Bubble shape classes
  const bubbleClasses = isCurrentUser
    ? `bg-[#1784FF] text-white ${isClusteredWithPrev ? 'rounded-tr-[6px]' : 'rounded-tr-2xl'} ${isClusteredWithNext ? 'rounded-br-[6px]' : 'rounded-br-[4px]'}`
    : `bg-[#2A2A2D] border border-[#3A3A3C] text-zinc-100 ${isClusteredWithPrev ? 'rounded-tl-[6px]' : 'rounded-tl-2xl'} ${isClusteredWithNext ? 'rounded-bl-[6px]' : 'rounded-bl-[4px]'}`;

  return (
    <>
      <div
        id={`msg-${message.id}`}
        className={`flex w-full ${isCurrentUser ? 'justify-end' : 'justify-start'} animate-in slide-in-from-bottom-1 fade-in duration-200 group ${
          isSearchFocused
            ? 'ring-2 ring-amber-400 rounded-2xl p-1 shadow-[0_0_16px_rgba(251,191,36,0.4)]'
            : ''
        }`}
      >
        <div
          className={`flex min-w-0 items-end gap-2.5 max-w-[90%] sm:max-w-[78%] md:max-w-[72%] xl:max-w-[65%]`}
        >
          {/* Avatar for other user */}
          {!isCurrentUser && (
            <div
              onClick={onAvatarClick}
              className={`w-8 h-8 shrink-0 z-10 ${onAvatarClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`}
            >
              {!isClusteredWithNext && (
                <ChatAvatar
                  src={senderAvatar}
                  name={pickDisplayName(message.sender_name) || 'User'}
                  size="sm"
                />
              )}
            </div>
          )}

          <div className="flex flex-col relative min-w-0">
            <div
              className={`relative ${paddingClass} text-[15px] font-normal shadow-sm leading-relaxed rounded-2xl ${bubbleClasses}`}
            >
              {/* Rich attachment previews (rendered first) */}
              <div className="relative">
                {renderAttachments()}
                {queueStatus === 'uploading' && (
                  <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px] rounded-xl flex items-center justify-center z-10">
                    <div className="relative w-12 h-12 rounded-full bg-black/60 border border-white/15 shadow-xl flex items-center justify-center shrink-0">
                      <svg className="w-12 h-12 -rotate-90 absolute inset-0" viewBox="0 0 36 36">
                        <circle
                          cx="18"
                          cy="18"
                          r="15.5"
                          fill="none"
                          stroke="rgba(255, 255, 255, 0.15)"
                          strokeWidth="3"
                        />
                        <circle
                          cx="18"
                          cy="18"
                          r="15.5"
                          fill="none"
                          stroke="#1784FF"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeDasharray={`${(uploadProgress || 0) * 0.975} 97.5`}
                          className="transition-all duration-300 ease-out"
                        />
                      </svg>
                      {uploadProgress && uploadProgress > 0 ? (
                        <span className="text-white text-[11px] font-bold tracking-tight select-none">
                          {Math.round(uploadProgress)}%
                        </span>
                      ) : (
                        <FiLoader className="animate-spin text-[#1784FF]" size={18} />
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Message caption text (rendered under attachments) */}
              {hasTextBody && (
                <div
                  className={`whitespace-pre-wrap break-words [overflow-wrap:anywhere] ${message.attachments && message.attachments.length > 0 ? 'mt-2.5 px-1 pb-1' : ''}`}
                >
                  {renderHighlightedText(message.body || '', searchQuery)}
                </div>
              )}

              {/* Float-overlayed timestamp for media-only, standard bottom timestamp for others */}
              {isImageOrVideoOnly ? (
                <div className="absolute bottom-2 right-2 z-20 px-2 py-0.5 rounded bg-black/60 backdrop-blur-[2px] text-[10px] text-white/90 font-medium select-none pointer-events-none flex items-center gap-1">
                  <span>{timeStr}</span>
                  {renderStatusIcon()}
                </div>
              ) : (
                <div
                  className={`flex items-center justify-end gap-1 mt-1.5 -mb-0.5 -mr-0.5 ${isCurrentUser ? 'text-blue-200' : 'text-zinc-500'}`}
                >
                  <span className="text-[10px] font-medium leading-none">{timeStr}</span>
                  {renderStatusIcon()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {previewAttachment && (
        <FullscreenMediaViewer
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
        />
      )}
    </>
  );
};
